declare global {
  interface Window {
    ApplePaySession?: {
      canMakePayments(): boolean;
      // Add other Apple Pay methods as needed
    };
    google?: {
      payments?: {
        api?: any;
      };
    };
  }
}

export type PaymentType = 'credit_card' | 'ideal' | 'paypal' | 'apple_pay' | 'google_pay';

export interface PaymentMethod {
  id: string;
  type: PaymentType;
  nickname: string;
  lastFour?: string;
  brand?: string;
  email?: string;
  bankName?: string;
  isDefault: boolean;
  createdAt: string;
}

export interface PaymentDetails {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cardholderName: string;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

export interface IDEALPaymentDetails {
  bank: string;
  accountHolder: string;
  country: string;
}

export interface PayPalPaymentDetails {
  email: string;
}

export interface PaymentResult {
  success: boolean;
  transactionId?: string;
  error?: string;
  redirectUrl?: string;
}

/**
 * Generate a unique payment method ID
 */
const generatePaymentMethodId = (): string => {
  return `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get all stored payment methods
 */
export const getPaymentMethods = (): PaymentMethod[] => {
  try {
    const stored = localStorage.getItem('manivo_payment_methods');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to parse payment methods:', error);
    return [];
  }
};

/**
 * Save a new payment method
 */
export const savePaymentMethod = (
  paymentDetails: PaymentDetails | IDEALPaymentDetails | PayPalPaymentDetails | {},
  nickname: string,
  isDefault: boolean = false,
  paymentType: PaymentType = 'credit_card'
): PaymentMethod => {
  const id = 'pm_' + Math.random().toString(36).substr(2, 9);
  const now = new Date().toISOString();
  
  let paymentMethod: PaymentMethod = {
    id,
    type: paymentType,
    nickname,
    isDefault,
    createdAt: now,
  };

  switch (paymentType) {
    case 'credit_card':
      const cardDetails = paymentDetails as PaymentDetails;
      paymentMethod.lastFour = cardDetails.cardNumber.slice(-4);
      paymentMethod.brand = getCardBrand(cardDetails.cardNumber);
      break;
    
    case 'ideal':
      const idealDetails = paymentDetails as IDEALPaymentDetails;
      paymentMethod.bankName = IDEAL_BANKS.find(bank => bank.code === idealDetails.bank)?.name || idealDetails.bank;
      break;
    
    case 'paypal':
      const paypalDetails = paymentDetails as PayPalPaymentDetails;
      paymentMethod.email = paypalDetails.email;
      break;
  }

  // Get existing payment methods
  const existingMethods = getPaymentMethods();
  
  // If this should be default, make others non-default
  if (isDefault) {
    existingMethods.forEach(method => method.isDefault = false);
  }
  
  // Add new method
  const updatedMethods = [...existingMethods, paymentMethod];
  localStorage.setItem('manivo_payment_methods', JSON.stringify(updatedMethods));
  
  return paymentMethod;
};

/**
 * Get default payment method
 */
export const getDefaultPaymentMethod = (): PaymentMethod | null => {
  const methods = getPaymentMethods();
  return methods.find(method => method.isDefault) || methods[0] || null;
};

/**
 * Set payment method as default
 */
export const setDefaultPaymentMethod = (paymentMethodId: string): void => {
  const methods = getPaymentMethods();
  methods.forEach(method => {
    method.isDefault = method.id === paymentMethodId;
  });
  localStorage.setItem('manivo_payment_methods', JSON.stringify(methods));
};

/**
 * Delete a payment method
 */
export const deletePaymentMethod = (paymentMethodId: string): void => {
  const methods = getPaymentMethods().filter(method => method.id !== paymentMethodId);
  localStorage.setItem('manivo_payment_methods', JSON.stringify(methods));
};

/**
 * Determine card brand from card number
 */
export const getCardBrand = (cardNumber: string): PaymentMethod['brand'] => {
  const number = cardNumber.replace(/\s/g, '');
  
  if (/^4/.test(number)) return 'visa';
  if (/^5[1-5]/.test(number) || /^2(2[2-9]|[3-6]|7[0-1]|720)/.test(number)) return 'mastercard';
  if (/^3[47]/.test(number)) return 'amex';
  if (/^6011|^64[4-9]|^65/.test(number)) return 'discover';
  
  return 'visa'; // Default fallback
};

/**
 * Validate card number using Luhn algorithm
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  const number = cardNumber.replace(/\s/g, '');
  
  if (!/^\d+$/.test(number)) return false;
  if (number.length < 13 || number.length > 19) return false;
  
  let sum = 0;
  let isEven = false;
  
  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i]);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

/**
 * Validate expiry date
 */
export const validateExpiryDate = (month: string, year: string): boolean => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  const expMonth = parseInt(month);
  const expYear = parseInt(year);
  
  if (expMonth < 1 || expMonth > 12) return false;
  if (expYear < currentYear) return false;
  if (expYear === currentYear && expMonth < currentMonth) return false;
  
  return true;
};

/**
 * Validate CVV
 */
export const validateCVV = (cvv: string, cardBrand?: PaymentMethod['brand']): boolean => {
  if (!/^\d+$/.test(cvv)) return false;
  
  if (cardBrand === 'amex') {
    return cvv.length === 4;
  } else {
    return cvv.length === 3;
  }
};

/**
 * Format card number for display
 */
export const formatCardNumber = (cardNumber: string): string => {
  const number = cardNumber.replace(/\s/g, '');
  const brand = getCardBrand(number);
  
  if (brand === 'amex') {
    return number.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  } else {
    return number.replace(/(\d{4})(?=\d)/g, '$1 ');
  }
};

/**
 * Mask card number for display
 */
export const maskCardNumber = (cardNumber: string): string => {
  const lastFour = cardNumber.slice(-4);
  return `**** **** **** ${lastFour}`;
};

// iDEAL Banks
export const IDEAL_BANKS = [
  { code: 'ABNANL2A', name: 'ABN AMRO' },
  { code: 'ASNBNL21', name: 'ASN Bank' },
  { code: 'BUNQNL2A', name: 'Bunq' },
  { code: 'INGBNL2A', name: 'ING' },
  { code: 'KNABNL2H', name: 'Knab' },
  { code: 'MOYONL21', name: 'Moneyou' },
  { code: 'RABONL2U', name: 'Rabobank' },
  { code: 'RBRBNL21', name: 'RegioBank' },
  { code: 'SNSBNL2A', name: 'SNS Bank' },
  { code: 'TRIONL2U', name: 'Triodos Bank' },
  { code: 'FVLBNL22', name: 'van Lanschot' },
];

// Process payment based on type
export const processPayment = async (
  amount: number,
  paymentDetails: PaymentDetails | IDEALPaymentDetails | PayPalPaymentDetails | {},
  isRecurring: boolean = false,
  paymentType: PaymentType = 'credit_card'
): Promise<PaymentResult> => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 2000));

  try {
    switch (paymentType) {
      case 'credit_card':
        return processCreditCardPayment(amount, paymentDetails as PaymentDetails, isRecurring);
      
      case 'ideal':
        return processIDEALPayment(amount, paymentDetails as IDEALPaymentDetails, isRecurring);
      
      case 'paypal':
        return processPayPalPayment(amount, paymentDetails as PayPalPaymentDetails, isRecurring);
      
      case 'apple_pay':
        return processApplePayPayment(amount, isRecurring);
      
      case 'google_pay':
        return processGooglePayPayment(amount, isRecurring);
      
      default:
        throw new Error('Unsupported payment method');
    }
  } catch (error) {
    console.error('Payment processing error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Payment failed'
    };
  }
};

const processCreditCardPayment = async (
  amount: number,
  paymentDetails: PaymentDetails,
  isRecurring: boolean
): Promise<PaymentResult> => {
  // Simulate more realistic payment processing
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // Add realistic validation and potential failures
  const cardNumber = paymentDetails.cardNumber.replace(/\s/g, '');
  
  // Simulate some payment failures for realism
  if (cardNumber.startsWith('4000000000000002')) {
    return {
      success: false,
      error: 'Your card was declined. Please try a different payment method.'
    };
  }
  
  if (cardNumber.startsWith('4000000000000069')) {
    return {
      success: false,
      error: 'Card has expired. Please check your expiry date.'
    };
  }
  
  if (cardNumber.startsWith('4000000000000127')) {
    return {
      success: false,
      error: 'Incorrect CVC. Please check your card security code.'
    };
  }
  
  // Simulate network/processing errors occasionally
  if (Math.random() < 0.05) { // 5% chance of processing error
    return {
      success: false,
      error: 'Payment processing temporarily unavailable. Please try again.'
    };
  }
  
  const transactionId = 'txn_' + Math.random().toString(36).substr(2, 9);
  
  return {
    success: true,
    transactionId
  };
};

const processIDEALPayment = async (
  amount: number,
  paymentDetails: IDEALPaymentDetails,
  isRecurring: boolean
): Promise<PaymentResult> => {
  if (isRecurring) {
    return {
      success: false,
      error: 'iDEAL does not support recurring payments'
    };
  }

  // Simulate bank validation
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  if (!paymentDetails.bank || !paymentDetails.accountHolder.trim()) {
    return {
      success: false,
      error: 'Please complete all required fields'
    };
  }

  const transactionId = 'ideal_' + Math.random().toString(36).substr(2, 9);
  
  // iDEAL requires redirect - don't process immediately
  return {
    success: false,
    error: 'Redirecting to your bank...',
    redirectUrl: `https://simulator.ideal.nl/payment?amount=${amount}&bank=${paymentDetails.bank}&reference=${transactionId}`
  };
};

const processPayPalPayment = async (
  amount: number,
  paymentDetails: PayPalPaymentDetails,
  isRecurring: boolean
): Promise<PaymentResult> => {
  // Simulate PayPal validation
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  if (!paymentDetails.email || !/\S+@\S+\.\S+/.test(paymentDetails.email)) {
    return {
      success: false,
      error: 'Please enter a valid PayPal email address'
    };
  }

  const transactionId = 'pp_' + Math.random().toString(36).substr(2, 9);
  
  // PayPal requires redirect - don't process immediately
  return {
    success: false,
    error: 'Redirecting to PayPal...',
    redirectUrl: `https://www.paypal.com/checkoutnow?amount=${amount}&currency=USD&reference=${transactionId}`
  };
};

const processApplePayPayment = async (
  amount: number,
  isRecurring: boolean
): Promise<PaymentResult> => {
  // Check if Apple Pay is available
  if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
    return {
      success: false,
      error: 'Apple Pay is not available on this device'
    };
  }

  // Simulate Apple Pay processing
  await new Promise(resolve => setTimeout(resolve, 2500));
  
  // Simulate potential Apple Pay cancellation
  if (Math.random() < 0.1) { // 10% chance user cancels
    return {
      success: false,
      error: 'Payment was cancelled'
    };
  }

  const transactionId = 'ap_' + Math.random().toString(36).substr(2, 9);
  
  return {
    success: true,
    transactionId
  };
};

const processGooglePayPayment = async (
  amount: number,
  isRecurring: boolean
): Promise<PaymentResult> => {
  // Check if Google Pay is available
  if (!window.google?.payments?.api) {
    return {
      success: false,
      error: 'Google Pay is not available'
    };
  }

  // Simulate Google Pay processing
  await new Promise(resolve => setTimeout(resolve, 2500));
  
  // Simulate potential Google Pay errors
  if (Math.random() < 0.08) { // 8% chance of error
    return {
      success: false,
      error: 'Google Pay payment failed. Please try another method.'
    };
  }

  const transactionId = 'gp_' + Math.random().toString(36).substr(2, 9);
  
  return {
    success: true,
    transactionId
  };
};

/**
 * Clear all payment methods (for testing)
 */
export const clearPaymentMethods = (): void => {
  localStorage.removeItem('manivo_payment_methods');
}; 